// components/PartnershipPost.tsx
"use client";

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link'; // Import Link for navigation
import { BountyDetail } from '@/lib/api/types';

// Shadcn UI Components
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, ArrowUpRight, Copy, Check, Target, DollarSign, ClipboardList } from 'lucide-react';
import { useToast } from '@/hooks/use-toast'; // Corrected path
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PartnershipPostProps extends BountyDetail {
  bounty_id: string;
  name: string;
  cpm: number;
  total_budget: number;
  budget_used: number;
  description: string;
  offer_copy: string;
  offer_link: string;
  image_url: string;
}

const PartnershipPost: React.FC<PartnershipPostProps> = ({
  bounty_id,
  name,
  cpm,
  total_budget,
  budget_used,
  description,
  offer_copy,
  offer_link,
  image_url,
}) => {
  const { toast } = useToast();
  const [isOfferCopied, setIsOfferCopied] = useState(false);

  const budgetPercentage = total_budget > 0 ? (budget_used / total_budget) * 100 : 0;
  const remainingBudget = total_budget - budget_used;
  const isBudgetDepleted = remainingBudget <= 0;
  
  const formattedOfferLink = offer_link && !offer_link.startsWith('http') ? `https://${offer_link}` : offer_link;


  return (
    <Card className="w-full overflow-hidden shadow-lg border border-border bg-card text-foreground">
      <div className="md:flex">
        {/* Image Section */}
        <div className="md:w-1/3 relative bg-muted shrink-0"> {/* Added shrink-0 */}
          {image_url ? (
            <Image
              src={image_url}
              alt={`${name} campaign image`}
              width={400}
              height={300}
              className="object-cover w-full h-48 md:h-full"
            />
          ) : (
            <div className="w-full h-48 md:h-full flex items-center justify-center bg-muted">
              <Target size={48} className="text-muted-foreground" />
            </div>
          )}
           {isBudgetDepleted && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-center p-4 z-10">
              <AlertTriangle size={48} className="text-yellow-400 mb-2" />
              <p className="text-xl font-semibold text-yellow-400">Budget Depleted</p>
              <p className="text-sm text-yellow-200">This bounty is no longer accepting new submissions.</p>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="md:w-2/3 flex flex-col">
          <CardHeader className="p-6">
            <div className="flex justify-between items-start gap-2">
              <CardTitle className="text-2xl font-bold tracking-tight leading-tight">{name}</CardTitle>
              <Badge variant={isBudgetDepleted ? "destructive" : "default"} className={`shrink-0 ${isBudgetDepleted ? '' : 'bg-green-600 hover:bg-green-700'}`}>
                {isBudgetDepleted ? "Closed" : "Active"}
              </Badge>
            </div>
            <CardDescription className="mt-2 text-muted-foreground text-base leading-relaxed line-clamp-3">
              {description}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6 space-y-6 flex-grow">
            {(offer_copy || offer_link) && (
              <div className="space-y-3 p-4 border border-dashed border-border rounded-lg bg-background/50">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Offer Details</h4>
                {offer_copy && (
                  <p className="text-foreground leading-relaxed whitespace-pre-wrap text-sm">
                    {offer_copy}
                  </p>
                )}
                <div className="flex items-center space-x-3 pt-2">
                  {formattedOfferLink && ( // Use the formatted link
                    <Button variant="secondary" size="sm" asChild>
                      <a href={formattedOfferLink} target="_blank" rel="noopener noreferrer">
                        Visit Offer<ArrowUpRight size={16} className="ml-2" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 border border-border rounded-lg bg-background/50 flex flex-col justify-between">
                <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase">CPM</p>
                    <p className="text-2xl font-semibold text-primary">
                    ${cpm.toFixed(2)}
                    </p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Per 1,000 views</p>
              </div>
              <div className="p-4 border border-border rounded-lg bg-background/50">
                <p className="text-xs text-muted-foreground font-medium uppercase">Budget Status</p>
                <div className="mt-1">
                  <div className="flex justify-between text-xs text-muted-foreground mb-0.5">
                    <span>${budget_used.toLocaleString()} Used</span>
                    <span>${total_budget.toLocaleString()} Total</span>
                  </div>
                  <Progress value={budgetPercentage} className="h-2 [&>div]:bg-primary" />
                   <p className={`text-xs mt-1 font-medium ${isBudgetDepleted ? 'text-destructive' : 'text-green-500'}`}>
                    {isBudgetDepleted ? "Budget fully utilized" : `$${remainingBudget.toLocaleString()} Remaining`}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="p-6 border-t border-border">
            {!isBudgetDepleted ? (
              <Button asChild className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link href={`/payments?bounty_id=${bounty_id}&name=${encodeURIComponent(name)}`}>
                  <ClipboardList size={18} className="mr-2" /> Submit Clips & View Payouts
                </Link>
              </Button>
            ) : (
              <Button className="w-full sm:w-auto" disabled>
                <AlertTriangle size={18} className="mr-2" /> Submissions Closed
              </Button>
            )}
          </CardFooter>
        </div>
      </div>
    </Card>
  );
};

export default PartnershipPost;